import type { CurvaDocument } from "@/document/model/document";
import { getActivePage, getAllSceneNodes, getSceneNodeChildren, findSceneNode } from "@/document/model/document";
import type { FrameNode, SceneNode } from "@/document/model/nodes";
import {
  createSceneTransformCache,
  getCachedSceneTransformCache,
  type ScenePreview,
  type SceneTransformCache,
} from "@/document/operations/nodeWorldTransform";
import { frameUsesPatternLayout, patternInstancesForFrame } from "@/document/operations/patternLayout";
import { normalizeStrokeStack } from "@/document/model/styles";
import { applyAffinePoint, boundsFromAffineRect, invertAffine } from "@/lib/math/affine";
import type { Rect } from "@/lib/math/rect";
import { unionRects } from "@/lib/math/rect";
import { rectCenter, rotatePoint, type Point } from "@/lib/math/rotation";
import type { ResizeHandle, RotationHandle } from "@/lib/math/selectionHandles";
import { getResizeHandlePositions } from "@/lib/math/selectionHandles";
import { nodeIntersectsLasso, nodeIntersectsMarquee, pointHitsNode, rectContainsRect } from "./shapeHitTest";

export type { ResizeHandle, RotationHandle } from "@/lib/math/selectionHandles";
export { getResizeHandlePositions } from "@/lib/math/selectionHandles";

export type HitResult =
  | { type: "resizeHandle"; handle: ResizeHandle }
  | { type: "node"; nodeId: string }
  | { type: "empty" };

export function hitTestDocument(
  document: CurvaDocument,
  point: { x: number; y: number },
  selectionBounds: Rect | undefined,
  zoom: number,
  options: {
    deepSelect?: boolean;
    selectedNodeIds?: string[];
    activePageId?: string;
    viewportBounds?: Rect;
    coarseGeometry?: boolean;
    selectionPreviews?: ScenePreview[];
  } = {},
): HitResult {
  const transformCache = getCachedSceneTransformCache(document, options.selectionPreviews ?? []);
  const selectedIds = new Set(options.selectedNodeIds ?? []);
  const editableSelection = [...selectedIds].every(nodeId => !transformCache.isLockedForEditing(nodeId));
  const handle = selectionBounds && editableSelection
    ? hitTestSelectionResizeHandle(document, [...selectedIds], selectionBounds, point, zoom, options.selectionPreviews ?? [])
    : undefined;
  if (handle) {
    return { type: "resizeHandle", handle };
  }

  const hitTolerance = 4 / zoom;
  const expandedViewport = options.viewportBounds ? expandRect(options.viewportBounds, hitTolerance) : undefined;
  const visibleNodes = expandedViewport
    ? visibleSceneNodesInViewport(document, options.activePageId, transformCache, expandedViewport)
    : visibleSceneNodes(document, options.activePageId);
  const occludingFrame = findTopmostFrameAtPoint(document, point, options.activePageId, transformCache, expandedViewport);
  const candidates = visibleNodes;
  const occludedCandidates = occludingFrame
    ? candidates.filter(node => transformCache.isDescendantOf(node.id, occludingFrame.id))
    : candidates;
  const nodeMatchesPoint = (node: SceneNode): boolean => {
    if (!pointInRect(point, getNodeInteractionBounds(document, node, transformCache))) {
      return false;
    }
    // A shape that overflows a clipping frame is not painted outside it, so it
    // must not be hittable there either.
    if (!pointWithinClippingAncestors(document, node.id, point, transformCache)) {
      return false;
    }
    if (options.coarseGeometry) {
      return true;
    }
    return pointHitsNodeOrPatternCopy(document, node, point, hitTolerance, transformCache);
  };
  const hitNode = [...occludedCandidates]
    .reverse()
    .filter(node => node.type !== "frame" && node.visible && !transformCache.isLockedForEditing(node.id))
    .find(node => {
      if (!nodeMatchesPoint(node)) {
        return false;
      }

      const booleanAncestor = transformCache.nearestBooleanAncestor(node.id);
      if (!booleanAncestor) {
        return true;
      }

      if (selectedIds.has(node.id)) {
        return true;
      }

      return pointInRect(point, getNodeInteractionBounds(document, booleanAncestor, transformCache)) && nodeMatchesPoint(booleanAncestor);
    });
  if (!hitNode) {
    const siblingContainerId = !options.deepSelect ? findSiblingContainerAtPoint(document, point, selectedIds, transformCache) : undefined;
    if (siblingContainerId && !transformCache.isLockedForEditing(siblingContainerId)) {
      return { type: "node", nodeId: siblingContainerId };
    }

    if (occludingFrame && !transformCache.isLockedForEditing(occludingFrame.id)) {
      const isTopLevelFrame = isTopLevelFrameId(document, occludingFrame.id, options.activePageId);
      if (isTopLevelFrame) {
        if (options.deepSelect) {
          return { type: "node", nodeId: occludingFrame.id };
        }
      } else if (!options.deepSelect) {
        const nodeId = findNearestSelectionContainerAncestorId(document, occludingFrame.id) ?? occludingFrame.id;
        return { type: "node", nodeId };
      }
    }

    const selectedFrame = !options.deepSelect
      ? [...visibleNodes]
          .reverse()
          .find(node =>
            node.type === "frame" &&
            node.visible &&
            !transformCache.isLockedForEditing(node.id) &&
            selectedIds.has(node.id) &&
            pointInRect(point, getNodeInteractionBounds(document, node, transformCache)) &&
            pointHitsNode(document, node, point, hitTolerance, transformCache)
          )
      : undefined;
    return selectedFrame ? { type: "node", nodeId: selectedFrame.id } : { type: "empty" };
  }

  if (options.deepSelect || selectedIds.has(hitNode.id) || selectionIsInsideSameContainer(document, hitNode.id, selectedIds)) {
    return { type: "node", nodeId: hitNode.id };
  }

  const selectedContainerAncestorId = findSelectedPromotingContainerAncestorId(document, hitNode.id, selectedIds);
  if (selectedContainerAncestorId && !transformCache.isLockedForEditing(selectedContainerAncestorId)) {
    return { type: "node", nodeId: selectedContainerAncestorId };
  }

  const nodeId = findNearestSelectionContainerAncestorId(document, hitNode.id) ?? hitNode.id;
  return transformCache.isLockedForEditing(nodeId) ? { type: "empty" } : { type: "node", nodeId };
}

export function hitTestDirectChildOfContainer(
  document: CurvaDocument,
  containerId: string,
  point: { x: number; y: number },
  zoom: number,
): string | undefined {
  const transformCache = createSceneTransformCache(document);
  const container = getAllSceneNodes(document).find(node => node.id === containerId);
  if (!container || !isNodeVisibleInTree(document, container.id)) {
    return undefined;
  }

  const children = container ? getSceneNodeChildren(container) : undefined;
  if (!children) {
    return undefined;
  }

  const hitTolerance = 4 / zoom;
  for (const child of [...children].reverse()) {
    if (!child.visible || transformCache.isLockedForEditing(child.id)) {
      continue;
    }

    if (nodeOrDescendantHits(document, child, point, hitTolerance, transformCache)) {
      return child.id;
    }
  }

  return undefined;
}

export function getNodesInMarquee(document: CurvaDocument, marquee: Rect, activePageId?: string): string[] {
  const transformCache = createSceneTransformCache(document);
  const normalizedMarquee = normalizeRect(marquee);
  const frameHitIds = getTopLevelFrameIdsInMarquee(document, normalizedMarquee, activePageId, transformCache);
  const excludedDescendantIds = descendantIdsForNodes(document, frameHitIds);
  const nonFrameHitIds = visibleSceneNodes(document, activePageId)
    .filter(node => node.type !== "frame" && node.visible && !transformCache.isLockedForEditing(node.id))
    .filter(node => !excludedDescendantIds.has(node.id))
    .filter(node => rectsIntersect(normalizedMarquee, getNodeInteractionBounds(document, node, transformCache)) && nodeIntersectsMarquee(document, node, normalizedMarquee, transformCache))
    .map(node => node.id);
  return promoteGroupDescendants(document, [...frameHitIds, ...nonFrameHitIds], transformCache);
}

export function getNodesInLasso(document: CurvaDocument, lasso: Point[], activePageId?: string): string[] {
  if (lasso.length < 3) {
    return [];
  }

  const transformCache = createSceneTransformCache(document);
  const lassoBounds = boundsFromPoints(lasso);
  const nonFrameHitIds = visibleSceneNodes(document, activePageId)
    .filter(node => node.type !== "frame" && node.visible && !transformCache.isLockedForEditing(node.id))
    .filter(node => rectsIntersect(lassoBounds, getNodeInteractionBounds(document, node, transformCache)) && nodeIntersectsLasso(document, node, lasso, transformCache))
    .map(node => node.id);
  return promoteGroupDescendants(document, nonFrameHitIds, transformCache);
}

function descendantIdsForNodes(document: CurvaDocument, rootNodeIds: string[]): Set<string> {
  const ids = new Set<string>();
  for (const rootNodeId of rootNodeIds) {
    const root = findSceneNode(document, rootNodeId);
    if (!root) {
      continue;
    }

    collectDescendantIds(root, ids);
  }
  return ids;
}

function collectDescendantIds(node: SceneNode, ids: Set<string>): void {
  const children = getSceneNodeChildren(node);
  if (!children) {
    return;
  }

  for (const child of children) {
    ids.add(child.id);
    collectDescendantIds(child, ids);
  }
}

function getTopLevelFrameIdsInMarquee(
  document: CurvaDocument,
  marquee: Rect,
  activePageId: string | undefined,
  transformCache: SceneTransformCache,
): string[] {
  const page = getActivePage(document, activePageId);
  if (!page) {
    return [];
  }

  const frames = page.children.filter(
    (node): node is Extract<SceneNode, { type: "frame" }> =>
      node.type === "frame" && node.visible && !transformCache.isLockedForEditing(node.id),
  );
  const frameBounds = new Map(frames.map(frame => [frame.id, getNodeInteractionBounds(document, frame, transformCache)] as const));
  const fullyEnclosedFrames = frames.filter(frame => {
    const bounds = frameBounds.get(frame.id);
    return bounds && rectsIntersect(marquee, bounds) && rectContainsRect(marquee, bounds);
  });

  if (fullyEnclosedFrames.length === 0) {
    return [];
  }

  const fullyEnclosedIds = new Set(fullyEnclosedFrames.map(frame => frame.id));
  const frameIds: string[] = [];

  for (const frame of frames) {
    const bounds = frameBounds.get(frame.id);
    if (!bounds || !rectsIntersect(marquee, bounds)) {
      continue;
    }

    if (fullyEnclosedIds.has(frame.id)) {
      frameIds.push(frame.id);
      continue;
    }

    const overlapsFullyEnclosedFrame = fullyEnclosedFrames.some(enclosed => {
      if (enclosed.id === frame.id) {
        return false;
      }

      const enclosedBounds = frameBounds.get(enclosed.id);
      return enclosedBounds ? rectsOverlapWithPositiveArea(bounds, enclosedBounds) : false;
    });
    if (overlapsFullyEnclosedFrame) {
      continue;
    }

    if (nodeIntersectsMarquee(document, frame, marquee, transformCache)) {
      frameIds.push(frame.id);
    }
  }

  return frameIds;
}

export function getSelectionBounds(document: CurvaDocument, selectedNodeIds: string[]): Rect | undefined {
  if (selectedNodeIds.length === 0) {
    return undefined;
  }

  const selected = getAllSceneNodes(document).filter(node => selectedNodeIds.includes(node.id));
  return getNodesBounds(document, selected, createSceneTransformCache(document));
}

export function getNodesBounds(document: CurvaDocument, nodes: SceneNode[], transformCache?: SceneTransformCache): Rect | undefined {
  if (nodes.length === 0) {
    return undefined;
  }

  const cache = transformCache ?? createSceneTransformCache(document);
  const bounds = nodes.map(node => getNodeInteractionBounds(document, node, cache));
  const left = Math.min(...bounds.map(rect => rect.x));
  const top = Math.min(...bounds.map(rect => rect.y));
  const right = Math.max(...bounds.map(rect => rect.x + rect.width));
  const bottom = Math.max(...bounds.map(rect => rect.y + rect.height));
  return { x: left, y: top, width: right - left, height: bottom - top };
}

export function offsetBounds(bounds: Rect, dx: number, dy: number): Rect {
  return {
    ...bounds,
    x: bounds.x + dx,
    y: bounds.y + dy,
  };
}

export function resizeBounds(bounds: Rect, handle: ResizeHandle, dx: number, dy: number): Rect {
  const left = bounds.x;
  const right = bounds.x + bounds.width;
  const top = bounds.y;
  const bottom = bounds.y + bounds.height;
  const movingLeft = handle.includes("w") ? left + dx : left;
  const movingRight = handle.includes("e") ? right + dx : right;
  const movingTop = handle.includes("n") ? top + dy : top;
  const movingBottom = handle.includes("s") ? bottom + dy : bottom;

  const x1 = Math.min(movingLeft, movingRight);
  const x2 = Math.max(movingLeft, movingRight);
  const y1 = Math.min(movingTop, movingBottom);
  const y2 = Math.max(movingTop, movingBottom);
  const minSize = 0;

  return {
    x: x1,
    y: y1,
    width: Math.max(minSize, x2 - x1),
    height: Math.max(minSize, y2 - y1),
  };
}

export function transformBoundsInGroup(bounds: Rect, fromGroup: Rect, toGroup: Rect): Rect {
  const scaleX = fromGroup.width === 0 ? 1 : toGroup.width / fromGroup.width;
  const scaleY = fromGroup.height === 0 ? 1 : toGroup.height / fromGroup.height;
  return {
    x: toGroup.x + (bounds.x - fromGroup.x) * scaleX,
    y: toGroup.y + (bounds.y - fromGroup.y) * scaleY,
    width: Math.max(0, bounds.width * scaleX),
    height: Math.max(0, bounds.height * scaleY),
  };
}

export function normalizeRect(rect: Rect): Rect {
  const x1 = Math.min(rect.x, rect.x + rect.width);
  const x2 = Math.max(rect.x, rect.x + rect.width);
  const y1 = Math.min(rect.y, rect.y + rect.height);
  const y2 = Math.max(rect.y, rect.y + rect.height);
  return { x: x1, y: y1, width: x2 - x1, height: y2 - y1 };
}

function promoteGroupDescendants(document: CurvaDocument, nodeIds: string[], transformCache: SceneTransformCache): string[] {
  const promoted = new Set<string>();
  const ordered: string[] = [];
  for (const nodeId of nodeIds) {
    const promotedId = nearestSelectionContainerAncestorId(document, nodeId) ?? nodeId;
    if (promoted.has(promotedId) || transformCache.isLockedForEditing(promotedId)) {
      continue;
    }

    promoted.add(promotedId);
    ordered.push(promotedId);
  }
  return ordered;
}

function visibleSceneNodes(document: CurvaDocument, activePageId?: string): SceneNode[] {
  const page = getActivePage(document, activePageId);
  return page ? visibleSceneNodesInTree(page.children) : [];
}

function visibleSceneNodesInViewport(
  document: CurvaDocument,
  activePageId: string | undefined,
  transformCache: SceneTransformCache,
  expandedViewport: Rect,
): SceneNode[] {
  const page = getActivePage(document, activePageId);
  if (!page) {
    return [];
  }

  const nodes: SceneNode[] = [];
  collectViewportVisibleSceneNodes(page.children, document, transformCache, expandedViewport, nodes);
  return nodes;
}

function collectViewportVisibleSceneNodes(
  nodes: SceneNode[],
  document: CurvaDocument,
  transformCache: SceneTransformCache,
  expandedViewport: Rect,
  out: SceneNode[],
): void {
  for (const node of nodes) {
    if (!node.visible) {
      continue;
    }

    const subtree = transformCache.subtreeInteractionBounds(node.id);
    if (subtree && !rectsIntersect(subtree, expandedViewport)) {
      continue;
    }

    out.push(node);
    const children = getSceneNodeChildren(node);
    if (children) {
      collectViewportVisibleSceneNodes(children, document, transformCache, expandedViewport, out);
    }
  }
}

function visibleSceneNodesInTree(nodes: SceneNode[]): SceneNode[] {
  return nodes.flatMap(node => {
    if (!node.visible) {
      return [];
    }

    const children = getSceneNodeChildren(node);
    return children ? [node, ...visibleSceneNodesInTree(children)] : [node];
  });
}

function findTopmostFrameAtPoint(
  document: CurvaDocument,
  point: { x: number; y: number },
  activePageId?: string,
  transformCache: SceneTransformCache = createSceneTransformCache(document),
  expandedViewport?: Rect,
): SceneNode | undefined {
  const page = getActivePage(document, activePageId);
  if (!page) {
    return undefined;
  }

  return findTopmostFrameAtPointInNodes(document, page.children, point, transformCache, expandedViewport);
}

function findTopmostFrameAtPointInNodes(
  document: CurvaDocument,
  nodes: SceneNode[],
  point: { x: number; y: number },
  transformCache: SceneTransformCache,
  expandedViewport?: Rect,
): SceneNode | undefined {
  for (let index = nodes.length - 1; index >= 0; index -= 1) {
    const node = nodes[index]!;
    if (!node.visible || transformCache.isLockedForEditing(node.id) || node.type !== "frame") {
      continue;
    }

    if (expandedViewport) {
      const subtree = transformCache.subtreeInteractionBounds(node.id);
      if (subtree && !rectsIntersect(subtree, expandedViewport)) {
        continue;
      }
    }

    if (!pointInRect(point, getNodeInteractionBounds(document, node, transformCache)) || !pointHitsNode(document, node, point, 0, transformCache)) {
      continue;
    }

    const children = getSceneNodeChildren(node);
    const nested = children ? findTopmostFrameAtPointInNodes(document, children, point, transformCache, expandedViewport) : undefined;
    return nested ?? node;
  }

  return undefined;
}

function isNodeVisibleInTree(document: CurvaDocument, nodeId: string): boolean {
  return document.pages.some(page => isNodeVisibleInNodes(page.children, nodeId, true));
}

function isNodeVisibleInNodes(nodes: SceneNode[], nodeId: string, ancestorsVisible: boolean): boolean {
  for (const node of nodes) {
    const visible = ancestorsVisible && node.visible;
    if (node.id === nodeId) {
      return visible;
    }

    const children = getSceneNodeChildren(node);
    if (!children) {
      continue;
    }

    if (isNodeVisibleInNodes(children, nodeId, visible)) {
      return true;
    }
  }

  return false;
}

function nearestSelectionContainerAncestorId(document: CurvaDocument, nodeId: string): string | undefined {
  for (const page of document.pages) {
    const ancestor = nearestSelectionContainerAncestorIdInNodes(page.children, nodeId);
    if (ancestor) {
      return ancestor;
    }
  }
  return undefined;
}

function nearestSelectionContainerAncestorIdInNodes(nodes: SceneNode[], nodeId: string, containerAncestorId?: string): string | undefined {
  for (const node of nodes) {
    if (node.id === nodeId) {
      return containerAncestorId;
    }

    const children = getSceneNodeChildren(node);
    if (!children) {
      continue;
    }

    const ancestor = nearestSelectionContainerAncestorIdInNodes(children, nodeId, isPromotingSelectionNode(node) ? node.id : containerAncestorId);
    if (ancestor) {
      return ancestor;
    }
  }
  return undefined;
}

function findResizeHandle(bounds: Rect, x: number, y: number, zoom: number): ResizeHandle | undefined {
  const size = 8 / zoom;
  return getResizeHandlePositions(bounds).find(handle => {
    return (
      x >= handle.x - size / 2 &&
      x <= handle.x + size / 2 &&
      y >= handle.y - size / 2 &&
      y <= handle.y + size / 2
    );
  })?.handle;
}

export function hitTestSelectionResizeHandle(
  document: CurvaDocument,
  selectedNodeIds: string[],
  selectionBounds: Rect | undefined,
  point: { x: number; y: number },
  zoom: number,
  previews: ScenePreview[] = [],
): ResizeHandle | undefined {
  if (!selectionBounds) {
    return undefined;
  }

  const transformCache = createSceneTransformCache(document, previews);
  if (selectedNodeIds.length === 1) {
    const nodeId = selectedNodeIds[0]!;
    if (transformCache.hasCompositeSelectionRotation(nodeId)) {
      const node = transformCache.getNode(nodeId);
      if (node) {
        const localBounds = transformCache.localBounds(node);
        const localPoint = transformCache.pointToLocal(nodeId, point);
        return findResizeHandle(localBounds, localPoint.x, localPoint.y, zoom);
      }
    }
  }

  return findResizeHandle(selectionBounds, point.x, point.y, zoom);
}

export function hitTestSelectionRotationHandle(
  document: CurvaDocument,
  selectedNodeIds: string[],
  selectionBounds: Rect | undefined,
  point: { x: number; y: number },
  zoom: number,
  rotation = 0,
  origin?: { x: number; y: number },
  previews: ScenePreview[] = [],
): RotationHandle | undefined {
  if (!selectionBounds) {
    return undefined;
  }

  const transformCache = getCachedSceneTransformCache(document, previews);
  const radius = 12 / zoom;
  const handles = selectedNodeIds.length === 1 && transformCache.hasCompositeSelectionRotation(selectedNodeIds[0]!)
    ? getRotationHandlePositionsFromCorners(
      transformCache.selectionCorners(selectedNodeIds[0]!) ?? [],
      zoom,
    )
    : getRotationHandlePositions(selectionBounds, zoom, rotation, origin);

  return handles.find(handle => Math.hypot(point.x - handle.x, point.y - handle.y) <= radius)?.handle;
}

export function hitTestResizeHandle(bounds: Rect | undefined, point: { x: number; y: number }, zoom: number): ResizeHandle | undefined {
  return bounds ? findResizeHandle(bounds, point.x, point.y, zoom) : undefined;
}

export function hitTestRotationHandle(
  bounds: Rect | undefined,
  point: { x: number; y: number },
  zoom: number,
  rotation = 0,
  origin?: { x: number; y: number },
): RotationHandle | undefined {
  if (!bounds) {
    return undefined;
  }

  const radius = 12 / zoom;
  return getRotationHandlePositions(bounds, zoom, rotation, origin).find(handle => {
    return Math.hypot(point.x - handle.x, point.y - handle.y) <= radius;
  })?.handle;
}

export function getRotationHandlePositionsFromCorners(corners: Point[], zoom: number): Array<{ handle: RotationHandle; x: number; y: number }> {
  if (corners.length < 4) {
    return [];
  }

  const center = {
    x: corners.reduce((sum, point) => sum + point.x, 0) / corners.length,
    y: corners.reduce((sum, point) => sum + point.y, 0) / corners.length,
  };
  const offset = 14 / zoom;
  const handles: RotationHandle[] = ["nw", "ne", "se", "sw"];

  return corners.slice(0, 4).map((corner, index) => {
    const dx = corner.x - center.x;
    const dy = corner.y - center.y;
    const length = Math.hypot(dx, dy) || 1;
    return {
      handle: handles[index]!,
      x: corner.x + (dx / length) * offset,
      y: corner.y + (dy / length) * offset,
    };
  });
}

export function getRotationHandlePositions(bounds: Rect, zoom: number, rotation = 0, origin = rectCenter(bounds)): Array<{ handle: RotationHandle; x: number; y: number }> {
  const center = origin;
  const offset = 14 / zoom;
  return [
    { handle: "nw" as const, x: bounds.x, y: bounds.y },
    { handle: "ne" as const, x: bounds.x + bounds.width, y: bounds.y },
    { handle: "se" as const, x: bounds.x + bounds.width, y: bounds.y + bounds.height },
    { handle: "sw" as const, x: bounds.x, y: bounds.y + bounds.height },
  ].map(handle => {
    const rotated = rotatePoint(handle, center, rotation);
    const dx = rotated.x - center.x;
    const dy = rotated.y - center.y;
    const length = Math.hypot(dx, dy) || 1;
    return {
      handle: handle.handle,
      x: rotated.x + (dx / length) * offset,
      y: rotated.y + (dy / length) * offset,
    };
  });
}

/**
 * Returns false when the point falls outside any ancestor frame that clips its
 * content, mirroring the renderer's clip so overflowing shapes can't be picked
 * in the empty space beyond their clipping frame.
 */
function pointWithinClippingAncestors(
  document: CurvaDocument,
  nodeId: string,
  point: { x: number; y: number },
  transformCache: SceneTransformCache,
): boolean {
  for (const ancestorId of transformCache.ancestorIds(nodeId)) {
    const ancestor = transformCache.getNode(ancestorId);
    if (ancestor?.type === "frame" && ancestor.clipsContent) {
      if (!pointHitsNode(document, ancestor, point, 0, transformCache)) {
        return false;
      }
    }
  }
  return true;
}

function pointInRect(point: { x: number; y: number }, rect: Rect): boolean {
  const normalized = normalizeRect(rect);
  return (
    point.x >= normalized.x &&
    point.x <= normalized.x + normalized.width &&
    point.y >= normalized.y &&
    point.y <= normalized.y + normalized.height
  );
}

export function getNodeInteractionBounds(
  document: CurvaDocument,
  node: SceneNode,
  transformCache: SceneTransformCache = createSceneTransformCache(document),
): Rect {
  const patternBounds = patternExpandedInteractionBounds(node, transformCache);
  const worldBounds = patternBounds ?? transformCache.interactionBounds(node);
  if (node.type === "primitive" && node.primitive.kind === "line") {
    return expandRect(worldBounds, Math.max(3, maxStrokeWidth(node)));
  }

  // Interaction bounds are only the broad phase. Expanding every vector
  // network is conservative, while checking whether each network is closed
  // would trace all of its regions on every pointermove.
  if (node.type === "vectorNetwork") {
    return expandRect(worldBounds, Math.max(3, maxStrokeWidth(node)));
  }

  return worldBounds;
}

function patternExpandedInteractionBounds(node: SceneNode, transformCache: SceneTransformCache): Rect | undefined {
  const parent = patternParentFrame(node.id, transformCache);
  if (!parent) {
    return undefined;
  }
  const local = transformCache.localBounds(node);
  const rects = patternInstancesForFrame(parent).map(instance => boundsFromAffineRect(instance.transform, local));
  return unionRects(rects) ?? undefined;
}

function patternParentFrame(nodeId: string, transformCache: SceneTransformCache): FrameNode | undefined {
  const parentId = transformCache.parentId(nodeId);
  if (!parentId) {
    return undefined;
  }
  const parent = transformCache.getNode(parentId);
  return parent?.type === "frame" && frameUsesPatternLayout(parent) ? parent : undefined;
}

function pointHitsNodeOrPatternCopy(
  document: CurvaDocument,
  node: SceneNode,
  point: { x: number; y: number },
  tolerance: number,
  transformCache: SceneTransformCache,
): boolean {
  const parent = patternParentFrame(node.id, transformCache);
  if (!parent) {
    return pointHitsNode(document, node, point, tolerance, transformCache);
  }

  for (const instance of patternInstancesForFrame(parent)) {
    const inverse = invertAffine(instance.transform);
    if (!inverse) {
      continue;
    }
    const localPoint = applyAffinePoint(inverse, point);
    if (pointHitsNode(document, node, localPoint, tolerance, transformCache)) {
      return true;
    }
  }
  return false;
}

function maxStrokeWidth(node: SceneNode): number {
  return Math.max(2, ...normalizeStrokeStack(node.style).map(stroke => stroke.width));
}

function boundsFromPoints(points: Array<{ x: number; y: number }>): Rect {
  if (points.length === 0) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  const left = Math.min(...points.map(point => point.x));
  const top = Math.min(...points.map(point => point.y));
  const right = Math.max(...points.map(point => point.x));
  const bottom = Math.max(...points.map(point => point.y));
  return { x: left, y: top, width: right - left, height: bottom - top };
}

function expandRect(rect: Rect, amount: number): Rect {
  return {
    x: rect.x - amount,
    y: rect.y - amount,
    width: rect.width + amount * 2,
    height: rect.height + amount * 2,
  };
}

function rectsIntersect(left: Rect, right: Rect): boolean {
  return (
    left.x <= right.x + right.width &&
    left.x + left.width >= right.x &&
    left.y <= right.y + right.height &&
    left.y + left.height >= right.y
  );
}

function rectsOverlapWithPositiveArea(left: Rect, right: Rect): boolean {
  const overlapLeft = Math.max(left.x, right.x);
  const overlapRight = Math.min(left.x + left.width, right.x + right.width);
  const overlapTop = Math.max(left.y, right.y);
  const overlapBottom = Math.min(left.y + left.height, right.y + right.height);
  return overlapRight > overlapLeft && overlapBottom > overlapTop;
}

function nodeOrDescendantHits(
  document: CurvaDocument,
  node: SceneNode,
  point: { x: number; y: number },
  hitTolerance: number,
  transformCache: SceneTransformCache,
): boolean {
  if (!node.visible) {
    return false;
  }

  if (!pointInRect(point, getNodeInteractionBounds(document, node, transformCache))) {
    return false;
  }

  if (pointHitsNode(document, node, point, hitTolerance, transformCache)) {
    return true;
  }

  const children = getSceneNodeChildren(node);
  if (!children) {
    return false;
  }

  return [...children]
    .reverse()
    .some(child => child.visible && !transformCache.isLockedForEditing(child.id) && nodeOrDescendantHits(document, child, point, hitTolerance, transformCache));
}

function findNearestSelectionContainerAncestorId(document: CurvaDocument, nodeId: string): string | undefined {
  const topLevelFrameIds = new Set(
    document.pages.flatMap(page => page.children.filter(node => node.type === "frame").map(node => node.id)),
  );
  for (const page of document.pages) {
    const ancestorId = findTopSelectionContainerAncestorInNodes(page.children, nodeId, [], topLevelFrameIds);
    if (ancestorId) {
      return ancestorId;
    }
  }

  return undefined;
}

function findSelectedPromotingContainerAncestorId(document: CurvaDocument, nodeId: string, selectedIds: Set<string>): string | undefined {
  const topLevelFrameIds = new Set(
    document.pages.flatMap(page => page.children.filter(node => node.type === "frame").map(node => node.id)),
  );
  for (const page of document.pages) {
    const ancestorId = findSelectedPromotingContainerAncestorInNodes(page.children, nodeId, [], selectedIds, topLevelFrameIds);
    if (ancestorId) {
      return ancestorId;
    }
  }

  return undefined;
}

function findSiblingContainerAtPoint(
  document: CurvaDocument,
  point: { x: number; y: number },
  selectedIds: Set<string>,
  transformCache: SceneTransformCache,
): string | undefined {
  for (const selectedId of selectedIds) {
    const selectedContainer = directSelectionContainerForNode(document, selectedId);
    if (!selectedContainer || selectedContainer.directChildId === selectedContainer.containerId) {
      continue;
    }

    const container = getAllSceneNodes(document).find(node => node.id === selectedContainer.containerId);
    if (!container || !isNodeVisibleInTree(document, container.id)) {
      continue;
    }

    const children = container ? getSceneNodeChildren(container) : undefined;
    const hitSibling = children
      ? [...children]
          .reverse()
          .find(child => (
            child.id !== selectedContainer.directChildId &&
            isSelectionContainer(child) &&
            child.visible &&
            !transformCache.isLockedForEditing(child.id) &&
            pointInRect(point, getNodeInteractionBounds(document, child, transformCache))
          ))
      : undefined;
    if (hitSibling) {
      return hitSibling.id;
    }
  }

  return undefined;
}

function findSelectedPromotingContainerAncestorInNodes(
  nodes: SceneNode[],
  nodeId: string,
  containerAncestors: string[],
  selectedIds: Set<string>,
  topLevelFrameIds: Set<string>,
): string | undefined {
  for (const node of nodes) {
    if (node.id === nodeId) {
      return [...containerAncestors].reverse().find(id => selectedIds.has(id));
    }

    const children = getSceneNodeChildren(node);
    if (!children) {
      continue;
    }

    const nextAncestors = isPromotingSelectionContainer(node, topLevelFrameIds.has(node.id)) ? [...containerAncestors, node.id] : containerAncestors;
    const ancestorId = findSelectedPromotingContainerAncestorInNodes(children, nodeId, nextAncestors, selectedIds, topLevelFrameIds);
    if (ancestorId) {
      return ancestorId;
    }
  }

  return undefined;
}

function findTopSelectionContainerAncestorInNodes(
  nodes: SceneNode[],
  nodeId: string,
  selectionAncestors: string[],
  topLevelFrameIds: Set<string>,
): string | undefined {
  for (const node of nodes) {
    if (node.id === nodeId) {
      return selectionAncestors[0];
    }

    const children = getSceneNodeChildren(node);
    if (!children) {
      continue;
    }

    const nextAncestors = isPromotingSelectionContainer(node, topLevelFrameIds.has(node.id)) ? [...selectionAncestors, node.id] : selectionAncestors;
    const ancestorId = findTopSelectionContainerAncestorInNodes(children, nodeId, nextAncestors, topLevelFrameIds);
    if (ancestorId) {
      return ancestorId;
    }
  }

  return undefined;
}

function selectionIsInsideSameContainer(document: CurvaDocument, hitNodeId: string, selectedIds: Set<string>): boolean {
  const hitContainer = directSelectionContainerForNode(document, hitNodeId);
  if (!hitContainer) {
    return false;
  }

  return [...selectedIds].some(selectedId => {
    const selectedContainer = directSelectionContainerForNode(document, selectedId);
    return selectedContainer?.containerId === hitContainer.containerId && selectedContainer.directChildId !== hitContainer.containerId;
  });
}

function directSelectionContainerForNode(document: CurvaDocument, nodeId: string): { containerId: string; directChildId: string } | undefined {
  for (const page of document.pages) {
    const found = directSelectionContainerForNodeInNodes(page.children, nodeId);
    if (found) {
      return found;
    }
  }
  return undefined;
}

function directSelectionContainerForNodeInNodes(
  nodes: SceneNode[],
  nodeId: string,
  container?: { containerId: string; directChildId: string },
): { containerId: string; directChildId: string } | undefined {
  for (const node of nodes) {
    if (node.id === nodeId) {
      return container;
    }

    const children = getSceneNodeChildren(node);
    if (!children) {
      continue;
    }

    const nextContainer = isSelectionContainer(node)
      ? { containerId: node.id, directChildId: node.id }
      : container;
    for (const child of children) {
      const childContainer = isSelectionContainer(node)
        ? { containerId: node.id, directChildId: child.id }
        : nextContainer;
      const found = directSelectionContainerForNodeInNodes([child], nodeId, childContainer);
      if (found) {
        return found;
      }
    }
  }
  return undefined;
}

function isSelectionContainer(node: SceneNode): boolean {
  return node.type === "frame" || node.type === "group" || node.type === "boolean" || node.type === "component" || node.type === "componentVariant" || node.type === "instance";
}

function isTopLevelFrameId(document: CurvaDocument, frameId: string, activePageId?: string): boolean {
  const page = getActivePage(document, activePageId);
  return page?.children.some(node => node.id === frameId && node.type === "frame") ?? false;
}

function isPromotingSelectionContainer(node: SceneNode, isTopLevelFrame = false): boolean {
  return isPromotingSelectionNode(node) || (node.type === "frame" && !isTopLevelFrame);
}

function isPromotingSelectionNode(node: SceneNode): boolean {
  return node.type === "group" || node.type === "boolean" || node.type === "component" || node.type === "componentVariant" || node.type === "instance";
}
